import {createContext, useCallback, useContext, useMemo, useReducer} from "react";
import {createSessionId} from "../orderUtils";
import {DEFAULT_LANG, localizeName, t} from "../i18n";

const PAYMENT_STORAGE_KEY = "kiosk_payment_recovery";

export const initialKioskState = {
  lang: DEFAULT_LANG,
  sessionId: null,
  stage: "idle",
  orderMethod: null,
  tableNumber: "",
  cart: [],
  memberMobile: "",
  couponCode: "",
  couponInfo: null,
  serverOrderId: null,
  orderNo: null,
  pricing: null,
  paymentMethod: null,
  paymentStatus: "idle",
  lastActivityAt: null,
};

export function kioskSessionReducer(state, action) {
  switch (action.type) {
    case "START":
      //保留客人在歡迎頁選好的語言，RESET（回歡迎頁）才回到預設語言
      return {...initialKioskState, lang: state.lang, sessionId: action.sessionId, stage: "choosing_service_mode", lastActivityAt: Date.now()};
    case "SET_LANG":
      return {...state, lang: action.lang, lastActivityAt: Date.now()};
    case "SET_METHOD":
      //內用帶桌號、外帶清空桌號（外帶的取餐依據是手機號碼）
      return {...state, orderMethod: action.method, tableNumber: action.tableNumber ?? "", stage: "browsing", pricing: null, lastActivityAt: Date.now()};
    case "ADD_ITEM":
      return {...state, cart: [...state.cart, action.item], stage: "browsing", pricing: null, lastActivityAt: Date.now()};
    case "UPDATE_ITEM":
      return {...state, cart: state.cart.map((item) => item.lineId === action.item.lineId ? action.item : item), pricing: null, lastActivityAt: Date.now()};
    case "REMOVE_ITEM":
      return {...state, cart: state.cart.filter((item) => item.lineId !== action.lineId), pricing: null, lastActivityAt: Date.now()};
    case "SET_QUANTITY":
      return {...state, cart: state.cart.map((item) => item.lineId === action.lineId ? {...item, qty: Math.max(1, action.qty)} : item), pricing: null, lastActivityAt: Date.now()};
    case "SET_MEMBER_MOBILE":
      return {...state, memberMobile: action.mobile, pricing: null, lastActivityAt: Date.now()};
    case "SET_COUPON":
      return {...state, couponCode: action.code, couponInfo: action.info ?? null, pricing: null, lastActivityAt: Date.now()};
    case "CLEAR_COUPON":
      return {...state, couponCode: "", couponInfo: null, pricing: null, lastActivityAt: Date.now()};
    case "SET_PRICING":
      return {...state, pricing: action.pricing, stage: "choosing_payment"};
    case "SET_SERVER_ORDER":
      return {...state, serverOrderId: action.order.order_id, orderNo: action.order.order_no, pricing: action.order.pricing ?? state.pricing, paymentMethod: action.paymentMethod, paymentStatus: action.order.payment_status ?? "pending", stage: action.stage};
    case "SET_PAYMENT_STATUS":
      return {...state, paymentStatus: action.status, stage: action.stage ?? state.stage};
    case "CLEAR_SERVER_ORDER":
      //取消訂單返回重選付款方式：清掉舊單資訊，購物車與報價保留
      return {...state, serverOrderId: null, orderNo: null, paymentMethod: null, paymentStatus: "idle", stage: "choosing_payment", lastActivityAt: Date.now()};
    case "TOUCH":
      return {...state, lastActivityAt: Date.now()};
    case "RESET":
      return initialKioskState;
    default:
      return state;
  }
}

const KioskSessionContext = createContext(null);

const restoreInitialState = () => {
  try {
    const recovery = JSON.parse(sessionStorage.getItem(PAYMENT_STORAGE_KEY) || "null");
    if (!recovery?.sessionId || !recovery?.orderId) return initialKioskState;
    return {
      ...initialKioskState,
      sessionId: recovery.sessionId,
      orderMethod: recovery.orderMethod ?? null,
      tableNumber: recovery.tableNumber ?? "",
      memberMobile: recovery.memberMobile ?? "",
      serverOrderId: recovery.orderId,
      orderNo: recovery.orderNo ?? null,
      pricing: recovery.pricing ?? null,
      paymentMethod: recovery.paymentMethod,
      paymentStatus: "recovering",
      stage: "recovering",
    };
  } catch {
    return initialKioskState;
  }
};

export function KioskSessionProvider({children}) {
  const [state, dispatch] = useReducer(kioskSessionReducer, initialKioskState, restoreInitialState);

  const startSession = useCallback(() => {
    sessionStorage.removeItem(PAYMENT_STORAGE_KEY);
    dispatch({type: "START", sessionId: createSessionId()});
  }, []);

  const resetSession = useCallback(() => {
    sessionStorage.removeItem(PAYMENT_STORAGE_KEY);
    dispatch({type: "RESET"});
  }, []);

  const saveRecovery = useCallback((data) => {
    sessionStorage.setItem(PAYMENT_STORAGE_KEY, JSON.stringify(data));
  }, []);

  const clearRecovery = useCallback(() => {
    sessionStorage.removeItem(PAYMENT_STORAGE_KEY);
  }, []);

  const getRecovery = useCallback(() => {
    try {
      return JSON.parse(sessionStorage.getItem(PAYMENT_STORAGE_KEY) || "null");
    } catch {
      return null;
    }
  }, []);

  const value = useMemo(() => ({state, dispatch, startSession, resetSession, saveRecovery, getRecovery, clearRecovery}), [state, startSession, resetSession, saveRecovery, getRecovery, clearRecovery]);
  return <KioskSessionContext.Provider value={value}>{children}</KioskSessionContext.Provider>;
}

export const useKioskSession = () => {
  const context = useContext(KioskSessionContext);
  if (!context) throw new Error("useKioskSession must be used inside KioskSessionProvider");
  return context;
};

//多語系便利 hook：tr(key, params) 取 UI 字串、name(item) 取商品名稱（ja→en→zh fallback）
export const useKioskLang = () => {
  const {state, dispatch} = useKioskSession();
  const lang = state.lang ?? DEFAULT_LANG;
  const setLang = useCallback((next) => dispatch({type: "SET_LANG", lang: next}), [dispatch]);
  const tr = useCallback((key, params) => t(lang, key, params), [lang]);
  const name = useCallback((item) => localizeName(item, lang), [lang]);
  return {lang, setLang, tr, name};
};
